---
title: "Fundamental Drivers: CPI, Interest Rates, News and Crypto Events"
slug: fundamental-drivers
shortTitle: "Fundamental Drivers"
description: "Why inflation data, interest rate decisions, jobs reports and crypto events move prices, how markets trade surprises against forecasts, and how to plan around news."
level: intermediate
category: markets
course: intermediate
order: 14
topics: [macro-news, bitcoin, ethereum]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/fundamental-drivers.svg
faqs:
  - q: "What is CPI and why do traders care about it?"
    a: "The Consumer Price Index measures how the prices of a basket of everyday goods and services change. It is a key inflation figure, and inflation shapes what central banks do with interest rates, which in turn moves currencies, gold, stocks and crypto."
  - q: "Why did the market fall on good news?"
    a: "Markets react to news compared with what was expected. If traders expected even better figures, or had already bought in anticipation, good news can still lead to selling. This is sometimes described as buy the rumor, sell the news."
  - q: "Does the Bitcoin halving push the price up?"
    a: "Nobody can say. The halving cuts the rate at which new bitcoin is created and is known years in advance. People disagree about how much of its effect is already priced in, and the handful of past halvings is far too small a sample to rely on."
  - q: "Should I trade during major news releases?"
    a: "Many traders avoid opening new positions just before a high-impact release, because spreads can widen and stops can fill at worse prices. If you do trade around news, use a smaller size and decide your plan before the figure comes out."
  - q: "Where can I see upcoming events on {{BRAND}}?"
    a: "The Economic calendar on the Markets page lists upcoming releases with their impact, forecast and previous figure. A market's own page shows upcoming events linked to it, and you can set a reminder before an event."
---

A chart shows you what the price did. **Fundamentals** help explain why. They are the economic data, central bank decisions, company results and industry events that change what buyers and sellers think an asset is worth. A surprise inflation figure can move gold, the dollar, stock indices and Bitcoin within the same minute.

You do not need to be an economist to use fundamentals. For most traders the practical questions are simpler: when is volatility scheduled, which releases matter for the markets I trade, and how do surprises tend to move prices? This lesson covers the main economic drivers, the events specific to crypto, and how to plan a trade around the news on {{BRAND}}.

<CTA label="Explore the markets" to="/markets" />

<KeyTakeaways>

- **Markets trade surprises, not headlines.** What moves the price is the actual figure compared with the forecast, not whether the news sounds good or bad.
- **Interest rates sit at the center.** Inflation and jobs data shape rate expectations, which ripple through the dollar, gold, stocks and crypto.
- **Crypto has its own calendar.** Halvings, network upgrades, token unlocks and regulatory decisions sit alongside the macro releases.
- **News means volatility.** Spreads can widen and stops can slip around big releases, so decide your plan before the figure comes out.

</KeyTakeaways>

## Expectations versus reality

Before each scheduled release, economists publish forecasts. By the time the number comes out, prices already reflect roughly what was expected. What moves the market is the **surprise**: the gap between the actual figure and the forecast.

On the **Economic calendar**, each event shows the **Est.** (the forecast), **Prev.** (the previous reading) and, once released, the **Actual** figure. If inflation was forecast at 3.1% and comes in at 3.4%, that is a hotter-than-expected surprise. If it comes in at 3.1%, the reaction may be small even though 3.1% sounds high.

This is why markets sometimes fall on good news. If traders bought ahead of an expected strong result, the release itself can trigger profit-taking. Details matter too: a headline figure might match the forecast while a revision to last month, or a sub-component, tells a different story.

## Inflation and interest rates

Interest rates are the thread that connects most macro news. A **central bank**, such as the US Federal Reserve, raises its policy rate to cool inflation and lowers it to support growth. Higher rates make cash and bonds pay more and borrowing cost more. That tends to strengthen the currency and weigh on assets that pay no interest, such as gold and crypto, and on companies valued for distant future growth. Lower rates tend to do the opposite.

Traders therefore watch every release that might change the path of rates. The **Consumer Price Index (CPI)** measures how the prices of a basket of goods and services change; **core CPI** strips out volatile food and energy. Rate decisions come with a statement and often a press conference, and the tone about future moves can matter more than the decision itself.

<CompareTable caption="Common scheduled releases and why traders watch them">

| Release | What it measures | Why it matters |
| --- | --- | --- |
| CPI (inflation) | Monthly change in consumer prices | Shapes interest rate expectations |
| Rate decision | The policy rate and the outlook | Moves currencies, gold, indices and crypto |
| US jobs report | Jobs added, unemployment, wages | Signals growth and wage-driven inflation |
| GDP | Total economic output | Confirms growth or contraction |
| PMI surveys | Business activity; above 50 means expansion | An early read on growth for indices, oil, copper |

</CompareTable>

These links are tendencies. A hot inflation figure often lifts the dollar and pressures gold and stock indices, but not every time. The same news can be read differently depending on what the market already expects.

## Crypto-specific events

Crypto reacts to macro data, and at times it has moved closely with technology stocks as a measure of risk appetite. It also has drivers of its own:

- **Halvings.** Roughly every four years, the number of new bitcoin created per block is cut in half. The date is known well in advance, and its effect on price is widely debated.
- **Network upgrades.** Changes to a blockchain's rules, such as Ethereum's upgrades, are scheduled but can be delayed, and sometimes draw attention for weeks beforehand.
- **Token unlocks.** Tokens held back for early investors or teams are released on a schedule, adding potential supply for some altcoins.
- **Regulation and legal decisions.** Approvals, bans, court rulings and tax rules in major countries can move the whole market.
- **Shocks.** Exchange failures, hacks and stablecoins losing their peg are unscheduled and can cause sharp falls.

For how traders read positioning and mood in crypto, see [Market Sentiment](/academy/article/market-sentiment).

## How a release can hit a position

<Example title="A gold buy through an inflation release (illustrative prices)">

You hold a buy of 0.05 lot of GOLD/USDT (5 ounces) from 2,400, with a stop loss at 2,385. Your planned loss is (2,400 − 2,385) × 5 = **75 USDT**.

- Inflation comes in hotter than forecast, and gold drops from 2,398 to 2,378 within a minute.
- Your stop triggers at 2,385 but fills at 2,381 in the fast market.
- Actual loss: (2,400 − 2,381) × 5 = **95 USDT**, 20 USDT more than planned.

</Example>

You could have closed before the release, or halved the size and set a wider stop for the same risk: a stop 30 dollars away on 2.5 ounces risks 30 × 2.5 = 75 USDT. Or you could have waited until the first move settled. None of these choices is right every time, but each is better made before the release than during it.

<Warning>

Around high-impact releases, spreads can widen sharply and prices can jump past several levels at once. A stop loss fills at market, so it can close at a worse price than you set, and leverage magnifies the result. Many traders avoid opening new positions in the minutes before a major release.

</Warning>

## Planning around news on {{BRAND}}

<Steps>
<Step title="Check the calendar">

Open the **Economic calendar** on the Markets page and switch the **Impact** filter to **High**. Each line shows the country, the event, the forecast and previous figure, and the markets it is linked to.

</Step>
<Step title="Set a reminder">

Use the bell next to an event to be reminded shortly before it. On a market's page, **Upcoming events** lists releases linked to that market.

</Step>
<Step title="Read the context">

A market's page also carries **News** and a **Why is it moving?** summary. Treat both as background, not as signals, and check important claims yourself.

</Step>
<Step title="Decide before the release">

Choose to close, reduce, or hold at a size where a fast move is acceptable, and make sure every open position has a stop loss.

</Step>
</Steps>

<Screenshot name="markets-economic-calendar" alt="The Economic calendar card on the Markets page with impact filter, forecast and previous figures" caption="The Economic calendar: filter by impact and set a reminder before a release." />

{/* TODO: verify with platform — calendar data coverage, reminder delivery options, and the wording of the AI-generated "Why is it moving?" summary. */}

<ProTip>

Write the week's high-impact releases into your trading plan on Monday morning, with a note on what you will do with open positions before each one. [Building a Trading Plan and Journal](/academy/article/trading-plan-and-journal) shows how to make this part of your routine.

</ProTip>

## Fundamentals and charts together

Fundamentals and technical analysis answer different questions. Fundamentals tell you when volatility is likely and what might change the market's mind. Charts give you the levels for entries, stops and targets. Many traders use the calendar to decide when to trade, or when to stand aside, and the chart to decide where. If you trade commodities or shares, the specific drivers for those markets are covered in [Trading Commodities CFDs](/academy/article/trading-commodities) and [Trading Stock CFDs](/academy/article/trading-stock-cfds).

## Conclusion

Fundamentals explain why prices move: inflation, interest rates, jobs and growth data on the macro side, and halvings, upgrades, unlocks and regulation in crypto. Markets react to surprises against expectations, not to headlines, and the same news can be read differently depending on what was already priced in. You do not need to forecast the figures. You need to know when they are due, how they can affect your positions, and what you will do before they arrive.
